'use client';

import { trpc } from '@/lib/trpc';

/**
 * Query hooks for the public leaderboard page.
 *
 * Caching strategy:
 * - Leaderboard data: 60s stale (ISR-aligned), keepPreviousData for smooth pagination
 * - Reference data (campaigns, events): 5min stale (rarely changes)
 * - All public data: refetchOnWindowFocus disabled
 */

const ONE_MINUTE = 60 * 1000;
const FIVE_MINUTES = 5 * 60 * 1000;

interface LeaderboardInput {
  campaignId: string;
  currencyCode: string;
  countryCode?: string;
  eventId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

interface CountryLeaderboardInput {
  campaignId: string;
  currencyCode: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

interface CityLeaderboardInput {
  campaignId: string;
  currencyCode: string;
  countryCode?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export function useLeaderboardRiders(input: LeaderboardInput | undefined) {
  return trpc.publicLeaderboard.riders.useQuery(input!, {
    enabled: !!input,
    staleTime: ONE_MINUTE,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });
}

export function useLeaderboardPillions(input: LeaderboardInput | undefined) {
  return trpc.publicLeaderboard.pillions.useQuery(input!, {
    enabled: !!input,
    staleTime: ONE_MINUTE,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });
}

export function useLeaderboardTeams(input: LeaderboardInput | undefined) {
  return trpc.publicLeaderboard.teams.useQuery(input!, {
    enabled: !!input,
    staleTime: ONE_MINUTE,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });
}

export function useLeaderboardParticipants(
  input: LeaderboardInput | undefined,
  initialData?: unknown,
) {
  return trpc.publicLeaderboard.participants.useQuery(input!, {
    enabled: !!input,
    staleTime: ONE_MINUTE,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
    ...(initialData !== undefined && { initialData: initialData as never }),
  });
}

interface ParticipantsSprintInput {
  campaignId: string;
  sprintStart: string;
  sprintEnd: string;
  currencyCode: string;
  page?: number;
  pageSize?: number;
}

export function useLeaderboardParticipantsSprint(input: ParticipantsSprintInput | undefined) {
  return trpc.publicLeaderboard.participantsSprint.useQuery(input!, {
    enabled: !!input,
    staleTime: ONE_MINUTE,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });
}

export function useLeaderboardCountries(input: CountryLeaderboardInput | undefined) {
  return trpc.publicLeaderboard.countries.useQuery(input!, {
    enabled: !!input,
    staleTime: ONE_MINUTE,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });
}

export function useLeaderboardCities(input: CityLeaderboardInput | undefined) {
  return trpc.publicLeaderboard.cities.useQuery(input!, {
    enabled: !!input,
    staleTime: ONE_MINUTE,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });
}

interface MyParticipantRankInput {
  entrantId: string;
  campaignId: string;
  currencyCode: string;
  countryCode?: string;
  eventId?: string;
  search?: string;
  entrantFilter?: 'all' | 'passenger';
  pageSize?: number;
}

export function useMyParticipantRank(input: MyParticipantRankInput | undefined) {
  return trpc.publicLeaderboard.myParticipantRank.useQuery(input!, {
    enabled: !!input,
    staleTime: ONE_MINUTE,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });
}

interface StatsInput {
  campaignId: string;
  currencyCode: string;
}

export function useLeaderboardStats(input: StatsInput | undefined, initialData?: unknown) {
  return trpc.publicLeaderboard.stats.useQuery(input!, {
    enabled: !!input,
    staleTime: FIVE_MINUTES,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
    ...(initialData !== undefined && { initialData: initialData as never }),
  });
}

export function useLeaderboardCampaigns(initialData?: unknown) {
  return trpc.publicLeaderboard.campaigns.useQuery(undefined, {
    staleTime: FIVE_MINUTES,
    refetchOnWindowFocus: false,
    ...(initialData !== undefined && { initialData: initialData as never }),
  });
}

export function useLeaderboardEvents(campaignId: string | undefined) {
  return trpc.publicLeaderboard.events.useQuery(
    { campaignId: campaignId! },
    {
      enabled: !!campaignId,
      staleTime: FIVE_MINUTES,
      refetchOnWindowFocus: false,
    },
  );
}
